import styled from '@emotion/styled';
import { DayPicker } from 'react-day-picker';
import { semantic_colors, colors, text_styles } from '../../foundation';
import { buildCSSWithLength, getAutoColorCode } from '../../styles';
import { BOTTOM_SHEET_TABLE_MARGIN, CELL_SIZE_MARGIN } from './hooks/constants';
import { DisplayMode } from './types';

export const DayPickerBase = styled(DayPicker)<{ cellSize: number; displayMode: DisplayMode }>`
  --rdp-cell-hover-color: ${getAutoColorCode(semantic_colors.background.surface).hover};
  --rdp-cell-active-color: ${getAutoColorCode(semantic_colors.background.surface).pressed};
  --rdp-cell-disabled-color: ${semantic_colors.content.disabled};
  --rdp-accent-color: ${semantic_colors.accent.primary};
  --rdp-accent-light-color: ${semantic_colors.accent.light};
  --rdp-background-color: ${semantic_colors.background.surface};
  --rdp-outline: 3px solid ${semantic_colors.tab_focus}; /* Outline border for focused elements */
  --rdp-outline-selected: 3px solid ${semantic_colors.tab_focus}; /* Outline border for focused _and_ selected elements */

  /* Hide elements for devices that are not screen readers */
  .rdp-vhidden {
    box-sizing: border-box;
    padding: 0;
    margin: 0;
    background: transparent;
    border: 0;
    -moz-appearance: none;
    -webkit-appearance: none;
    appearance: none;
    position: absolute !important;
    top: 0;
    width: 1px !important;
    height: 1px !important;
    padding: 0 !important;
    overflow: hidden !important;
    clip: rect(1px, 1px, 1px, 1px) !important;
    border: 0 !important;
  }

  /* Buttons */
  .rdp-button_reset {
    appearance: none;
    position: relative;
    margin: 0;
    padding: 0;
    cursor: default;
    color: inherit;
    background: none;
    font: inherit;

    -moz-appearance: none;
    -webkit-appearance: none;
  }

  .rdp-button_reset:focus-visible {
    /* Make sure to reset outline only when :focus-visible is supported */
    outline: none;
  }

  .rdp-button {
    border: 2px solid transparent;
    transition: all 0.1s linear;
  }

  .rdp-button[disabled]:not(.rdp-day_selected) {
    color: var(--rdp-cell-disabled-color);
  }

  .rdp-button[disabled]:not(.rdp-day_selected, .rdp-day_today) {
    border-radius: 0;
  }

  .rdp-button:not([disabled]) {
    cursor: pointer;
  }

  .rdp-button:focus-visible:not([disabled]) {
    color: inherit;
    background-color: var(--rdp-background-color);
    border: var(--rdp-outline);
  }

  .rdp-months {
    display: flex;
    column-gap: 20px;
  }

  .rdp-month {
    margin: 0;
  }

  .rdp-month:first-of-type {
    margin-left: 0;
  }

  .rdp-month:last-child {
    margin-right: 0;
  }

  .rdp-table {
    margin: ${({ displayMode }) => (displayMode === 'bottomSheet' ? `${BOTTOM_SHEET_TABLE_MARGIN}px 0 0` : 0)};
    max-width: calc(${({ cellSize }) => `${cellSize}px`} * 7);
    border-collapse: collapse;
  }

  .rdp-with_weeknumber .rdp-table {
    max-width: calc(${({ cellSize }) => `${cellSize}px`} * 8);
    border-collapse: collapse;
  }

  /* ---------- */
  /* Caption  */
  /* ---------- */

  .rdp-caption {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0;
    text-align: left;
  }

  .rdp-multiple_months .rdp-caption {
    position: relative;
    display: block;
    text-align: center;
  }

  .rdp-caption_dropdowns {
    position: relative;
    display: inline-flex;
  }

  .rdp-caption_label {
    position: relative;
    z-index: 1;
    display: block;
    margin: 0;
    padding: 0;
    white-space: nowrap;
    color: currentColor;
    border: 0;
    border: 2px solid transparent;
    font: inherit;
    text-align: center;
    color: var(--rdp-accent-color);
    ${text_styles.Body_14_Bold};
  }

  .rdp-nav {
    white-space: nowrap;
  }

  .rdp-caption_start .multiple-months {
    position: absolute;
    left: 0;
  }

  .rdp-caption_end .multiple-months {
    position: absolute;
    right: 0;
  }

  .rdp-caption_between .next-multiple-months,
  .rdp-caption_between .prev-multiple-months,
  .rdp-caption_start .next-multiple-months,
  .rdp-caption_end .prev-multiple-months {
    display: none;
  }

  .rdp-multiple_months .rdp-caption_start .rdp-nav {
    position: absolute;
    top: 50%;
    left: 0;
    transform: translateY(-50%);
  }

  .rdp-multiple_months .rdp-caption_end .rdp-nav {
    position: absolute;
    top: 50%;
    right: 0;
    transform: translateY(-50%);
  }

  .rdp-nav_button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    ${({ cellSize }) => `
      ${buildCSSWithLength('width', cellSize)};
      ${buildCSSWithLength('height', cellSize)};
    `}
    padding: 0;
    border-radius: 0;
  }

  /* ---------- */
  /* Dropdowns  */
  /* ---------- */

  .rdp-dropdown_year,
  .rdp-dropdown_month {
    position: relative;
    display: inline-flex;
    align-items: center;
  }

  .rdp-dropdown {
    appearance: none;
    position: absolute;
    z-index: 2;
    top: 0;
    bottom: 0;
    left: 0;
    width: 100%;
    margin: 0;
    padding: 0;
    cursor: inherit;
    opacity: 0;
    border: none;
    background-color: transparent;
    font-family: inherit;
    font-size: inherit;
    line-height: inherit;
  }

  .rdp-dropdown[disabled] {
    opacity: unset;
    color: unset;
  }

  .rdp-dropdown:focus-visible:not([disabled]) + .rdp-caption_label {
    background-color: var(--rdp-background-color);
    border: var(--rdp-outline);
    border-radius: 6px;
  }

  .rdp-dropdown_icon {
    margin: 0 0 0 5px;
  }

  .rdp-head {
    border: 0;
  }

  .rdp-head_row,
  .rdp-row {
    height: 100%;
  }

  .rdp-head_cell {
    ${({ cellSize, displayMode }) => `
      ${buildCSSWithLength('width', cellSize)};
      ${buildCSSWithLength('height', displayMode === 'bottomSheet' ? cellSize : cellSize - CELL_SIZE_MARGIN * 2)};
    `}
    vertical-align: middle;
    color: ${semantic_colors.content.secondary};
    ${text_styles.Body_12_SemiBold};
    text-transform: uppercase;
    text-align: center;
  }

  .rdp-tbody {
    border: 0;
  }

  .rdp-cell {
    position: relative;
    ${({ cellSize }) => `
      ${buildCSSWithLength('width', cellSize)};
      ${buildCSSWithLength('height', cellSize - CELL_SIZE_MARGIN * 2)};
    `}
    margin: ${CELL_SIZE_MARGIN}px 0;
    padding: 0;
    text-align: center;
  }

  .rdp-cell:has(.rdp-button[disabled]:not(.rdp-day_selected))::before {
    background-color: ${semantic_colors.background.disabled};
  }

  .rdp-cell:has(.rdp-day_range_start, .rdp-day_range_end) {
    position: relative;
  }

  .rdp-cell:has(.rdp-day_range_start, .rdp-day_range_end) .rdp-button {
    z-index: 1;
  }

  .rdp-cell:has(.rdp-day_range_start, .rdp-day_range_end)::after {
    content: '';
    position: absolute;
    top: ${CELL_SIZE_MARGIN}px;
    width: 50%;
    height: calc(100% - ${CELL_SIZE_MARGIN * 2}px);
    background-color: var(--rdp-accent-light-color);
    transition: all 0.1s linear;
  }

  .rdp-cell:has(.rdp-day_range_start.rdp-day_range_end)::after,
  .rdp-cell:has(.rdp-day_outside)::after {
    display: none;
  }

  .rdp-cell:has(.rdp-day_range_start)::after {
    left: 50%;
  }

  .rdp-cell:has(.rdp-day_range_end)::after {
    right: 50%;
  }

  .rdp-weeknumber,
  .rdp-day {
    display: flex;
    overflow: hidden;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    ${({ cellSize }) => `
      ${buildCSSWithLength('width', cellSize - CELL_SIZE_MARGIN * 2)};
      ${buildCSSWithLength('max-width', cellSize - CELL_SIZE_MARGIN * 2)};
      ${buildCSSWithLength('height', cellSize - CELL_SIZE_MARGIN * 2)};
      ${buildCSSWithLength('margin', CELL_SIZE_MARGIN)};
    `}
    border: 2px solid transparent;
    ${text_styles.Body_12_Medium};
  }

  .rdp-day:not(.rdp-day_range_middle),
  .rdp-day.rdp-day_outside,
  .rdp-day.rdp-day_today {
    border-radius: 100px;
  }

  .rdp-day:not([disabled]):not(.rdp-day_selected):not(.range-hover):hover {
    background-color: var(--rdp-cell-hover-color);
  }

  .rdp-day:not([disabled]):not(.rdp-day_selected):not(.range-hover):active {
    background-color: var(--rdp-cell-active-color);
  }

  .rdp-day_today:not(.rdp-day_outside) {
    border: 1px solid var(--rdp-accent-color);
  }

  .rdp-day_selected:not(.rdp-day_range_middle):not(.rdp-day_outside),
  .rdp-day_selected:not(.rdp-day_range_middle):not(.rdp-day_outside):focus-visible {
    color: white;
    opacity: 1;
    background-color: var(--rdp-accent-color);
  }

  .rdp-day_selected:not(.rdp-day_range_middle):not(.rdp-day_outside):hover {
    background-color: ${getAutoColorCode(semantic_colors.accent.primary).hover};
  }

  .rdp-day_selected:not(.rdp-day_range_middle):not(.rdp-day_outside):active {
    background-color: ${getAutoColorCode(semantic_colors.accent.primary).pressed};
  }

  .rdp-day_outside {
    color: var(--rdp-cell-disabled-color);
  }

  .rdp-day_selected:focus-visible {
    /* Since the background is the same use again the outline */
    outline: var(--rdp-outline);
    outline-offset: -3px;
    z-index: 1;
  }

  .rdp-day_selected:focus-visible:focus:not(:focus-visible) {
    outline: none;
  }

  .rdp-day_range_middle:not(.rdp-day_outside),
  .rdp-cell:has(.rdp-day_today:not(.rdp-day_outside).rdp-day_range_middle) {
    color: var(--rdp-accent-color);
  }

  .rdp-cell {
    position: relative;
  }

  .rdp-cell::before {
    content: '';
    position: absolute;
    width: 100%;
    height: calc(100% - ${CELL_SIZE_MARGIN * 2}px);
    top: ${CELL_SIZE_MARGIN}px;
    left: 0;
    transition: all 0.1s linear;
  }

  .rdp-cell:has(.rdp-day_disabled)::before {
    background-color: ${semantic_colors.background.disabled};
  }

  .rdp-cell:has(.range-hover:not(.rdp-day_outside)) {
    color: var(--rdp-accent-color);
  }

  .rdp-cell:has(.range-hover.rdp-day_range_start:not(.rdp-day_outside))::after,
  .rdp-cell:has(.range-hover.rdp-day_range_end:not(.rdp-day_outside))::after,
  .rdp-cell:has(.range-hover:not(.rdp-day_outside))::before,
  .rdp-cell:has(.rdp-day_range_middle:not(.rdp-day_outside))::before,
  .rdp-cell:has(.rdp-day_today:not(.rdp-day_outside).rdp-day_range_middle)::before {
    background-color: var(--rdp-accent-light-color);
  }

  .rdp-cell:has(.range-hover-inside.rdp-day_range_start:not(.rdp-day_outside))::after,
  .rdp-cell:has(.range-hover-inside.rdp-day_range_end:not(.rdp-day_outside))::after,
  .rdp-cell:has(.range-hover-inside.rdp-day_range_middle:not(.rdp-day_outside))::before,
  .rdp-cell:has(.rdp-day:not([disabled])):hover::after,
  .rdp-cell:has(.rdp-day_range_middle:not(.rdp-day_outside)):hover::before,
  .rdp-cell:has(.rdp-day_today:not(.rdp-day_outside).rdp-day_range_middle):hover::before {
    background-color: ${colors.blue100};
  }

  .rdp-cell:has(.range-hover:not(.rdp-day_outside)):active::before,
  .rdp-cell .rdp-day:not([disabled]):active::after,
  .rdp-cell:has(.rdp-day_range_middle:not(.rdp-day_outside)):active::before,
  .rdp-cell:has(.rdp-day_today:not(.rdp-day_outside).rdp-day_range_middle):active::before {
    background-color: ${colors.blue200};
  }

  &.show-all-hover-calendar {
    .rdp-cell:has(.range-hover.rdp-day_range_start)::after,
    .rdp-cell:has(.range-hover.rdp-day_range_end)::after,
    .rdp-cell:has(.range-hover)::before,
    .rdp-cell:has(.rdp-day_range_middle)::before,
    .rdp-cell:has(.rdp-day_range_start):has(.rdp-day_outside)::before,
    .rdp-cell:has(.rdp-day_range_end):has(.rdp-day_outside)::before {
      background-color: var(--rdp-accent-light-color);
    }

    .rdp-cell:has(.range-hover):active::before,
    .rdp-cell:has(.rdp-day_range_middle):active::before,
    .rdp-cell:has(.rdp-day_range_start):has(.rdp-day_outside):active::before,
    .rdp-cell:has(.rdp-day_range_end):has(.rdp-day_outside):active::before {
      background-color: var(--rdp-accent-light-color);
    }

    .rdp-cell:has(.range-hover-inside.rdp-day_range_start)::after,
    .rdp-cell:has(.range-hover-inside.rdp-day_range_end)::after,
    .rdp-cell:has(.range-hover-inside.rdp-day_range_middle)::before,
    .rdp-cell:has(.range-hover-inside.rdp-day_range_start.rdp-day_outside)::before,
    .rdp-cell:has(.range-hover-inside.rdp-day_range_end.rdp-day_outside)::before,
    .rdp-cell:has(.rdp-day_range_middle):hover::before,
    .rdp-cell:has(.rdp-day_range_start.rdp-day_outside):hover::before,
    .rdp-cell:has(.rdp-day_range_end.rdp-day_outside):hover::before {
      background-color: ${colors.blue100};
    }
  }

  &.unit-calendar {
    .rdp-cell:has(.rdp-day_range_start.rdp-day_range_end)::after,
    .rdp-cell:has(.rdp-day_outside)::after {
      display: initial;
    }
    .rdp-cell:has(.range-hover.rdp-day_range_start)::after,
    .rdp-cell:has(.range-hover.rdp-day_range_end)::after,
    .rdp-cell:has(.rdp-day_range_middle)::before {
      background-color: var(--rdp-accent-light-color);
    }
    .rdp-day_range_middle,
    .rdp-cell:has(.rdp-day_today.rdp-day_range_middle) {
      color: var(--rdp-accent-color);
    }

    .rdp-day_selected:not(.rdp-day_range_middle):hover {
      background-color: ${getAutoColorCode(semantic_colors.accent.primary).hover};
    }

    .rdp-day_selected:not(.rdp-day_range_middle):active {
      background-color: ${getAutoColorCode(semantic_colors.accent.primary).pressed};
    }
    .rdp-day_selected:not(.rdp-day_range_middle),
    .rdp-day_selected:not(.rdp-day_range_middle):focus-visible {
      color: white;
      opacity: 1;
      background-color: var(--rdp-accent-color);
    }
  }

  &.unit-week-calendar {
    .rdp-cell:has(.unit-hover-first)::before {
      border-radius: 100px 0 0 100px;
    }
    .rdp-cell:has(.unit-hover-last)::before {
      border-radius: 0 100px 100px 0;
    }
    .rdp-day:not([disabled]):not(.rdp-day_selected):not(.range-hover):hover,
    .rdp-day:not([disabled]):not(.rdp-day_selected):not(.range-hover):active {
      background-color: initial;
    }

    .rdp-cell:has(.unit-hover.rdp-day_range_start)::after,
    .rdp-cell:has(.unit-hover.rdp-day_range_end)::after,
    .rdp-cell:has(.unit-hover)::before {
      background-color: var(--rdp-cell-hover-color);
    }

    .rdp-cell:has(.unit-active)::before {
      background-color: var(--rdp-cell-active-color);
    }

    .rdp-cell:has(.unit-hover-inside.rdp-day_range_start)::after,
    .rdp-cell:has(.unit-hover-inside.rdp-day_range_end)::after,
    .rdp-cell:has(.unit-hover-inside.rdp-day_range_middle)::before,
    .rdp-cell:has(.rdp-day:not([disabled])):hover::after,
    .rdp-cell:has(.rdp-day_range_middle):hover::before,
    .rdp-cell:has(.rdp-day_today.rdp-day_range_middle):hover::before {
      background-color: var(--rdp-cell-hover-color);
    }

    .rdp-cell:has(.unit-hover):active::before,
    .rdp-cell .rdp-day:not([disabled]):active::after,
    .rdp-cell:has(.rdp-day_range_middle):active::before,
    .rdp-cell:has(.rdp-day_today.rdp-day_range_middle):active::before {
      background-color: var(--rdp-cell-hover-color);
    }

    .rdp-button[disabled]:not(.rdp-day_selected) {
      color: var(--rdp-cell-disabled-color);
    }
  }
`;
